<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import PlotlyLineChart from '@/components/projectDetail/SectionLineVisualisation/PlotlyLineChart.vue'
  import { debounce } from 'lodash'
  import { exportChartToHTML } from './exportChartToHTML.ts'
  import { useDrawerWidth } from '@/composables/map/useDrawerWidth'

  const projectStore = useProjectStore()
  const sectionLinesStore = useSectionLinesStore()
  const sectionLineVisualisationStore = useSectionLineVisualisationStore()
  const rasterFileUploadsStore = useRasterFileUploadStore()

  const { visualiseItem } = storeToRefs(sectionLinesStore)
  const { items: rasterFileUploads } = storeToRefs(rasterFileUploadsStore)
  const { lengthProfileGraphBar: graphBarConfig, isLengthProfileGraphBarShown } =
    storeToRefs(projectStore)
  const { writeLoading, currentItem: currentVisualisation } = storeToRefs(
    sectionLineVisualisationStore
  )

  const lengthProfileGraphBar = ref<HTMLElement | null>(null)
  const plotlyChart = ref<HTMLElement | null>(null)
  const allTraces = ref([])
  const layout = ref({})
  const { drawerWidth } = useDrawerWidth()

  const showLegend = ref(true)
  const style = computed(() => ({
    height: `${graphBarConfig.value.height}px`,
    width: `calc(100% - ${drawerWidth.value}px)`,
    marginLeft: `${drawerWidth.value}px`,
    backgroundColor: 'white',
    border: '0px solid #e6e6e6',
    borderWidth: '0 0 10px 0'
  }))
  const forceReloadKey = ref(0)

  async function resetSectionLine() {
    // force re-render the PlotlyLineChart component
    // by resetting the currentVisualisation to an empty object and then back
    const temp = currentVisualisation.value
    currentVisualisation.value = {}
    await nextTick()
    currentVisualisation.value = temp
  }

  onMounted(async () => {
    useResizeObserver(
      lengthProfileGraphBar,
      debounce(() => {
        if (
          Object.keys(currentVisualisation.value).length > 0 &&
          isLengthProfileGraphBarShown.value
        ) {
          console.log(graphBarConfig.value.height)
          plotlyChart.value?.resetChart(
            lengthProfileGraphBar.value?.clientWidth
              ? lengthProfileGraphBar.value?.clientWidth - 50
              : 0,
            graphBarConfig.value.height
          )
        }
      }, 200)
    )
    await resetSectionLine()
  })

  useResizeableElement({
    ref: lengthProfileGraphBar,
    widthProperty: 'lengthProfileGraphBar',
    disableResizeWidth: true
  })

  const createVisualisation = async () => {
    if (Object.keys(visualiseItem.value).length > 0) {
      await sectionLineVisualisationStore.createVisualisation(
        rasterFileUploads.value.map((item) => item.id)
      )
    }
  }

  const isVisualisationRefreshed = ref(false)
  watch(
    // check the cases for when
    // the visualiseItem is changed while the graph is shown
    // the visualiseItem is changed while the graph is hidden
    // the visualiseItem is the same while the graph is toggling between shown and hidden
    () => visualiseItem.value,
    async (newVisualiseItem, oldVisualiseItem) => {
      if (newVisualiseItem !== oldVisualiseItem) {
        if (graphBarConfig.value.shown) {
          await createVisualisation()
        }
        isVisualisationRefreshed.value = !!graphBarConfig.value.shown
      }
    }
  )

  watch(
    () => graphBarConfig.value.shown,
    async () => {
      if (graphBarConfig.value.shown && !isVisualisationRefreshed.value) {
        await createVisualisation()
        isVisualisationRefreshed.value = true
      }
    }
  )

  async function regenerateFigure() {
    forceReloadKey.value += 1
    await createVisualisation()
  }
</script>

<template>
  <div ref="lengthProfileGraphBar" :style="style" class="profile-graph-container">
    <div style="background-color: #e6e6e6; height: 100%" class="float-right d-flex flex-column">
      <v-tooltip text="Close Graph View">
        <template v-slot:activator="{ props }">
          <v-btn
            variant="plain"
            v-bind="props"
            icon="mdi-close-thick"
            @click="projectStore.toggleGraphType()"
          ></v-btn>
        </template>
      </v-tooltip>
      <v-tooltip text="Regenerate figure">
        <template v-slot:activator="{ props }">
          <v-btn variant="plain" v-bind="props" icon="mdi-reload" @click="regenerateFigure"></v-btn>
        </template>
      </v-tooltip>
      <v-tooltip>
        <template v-slot:activator="{ props }">
          <div v-bind="props" class="d-inline-block">
            <v-btn
              :disabled="allTraces.length === 0"
              variant="plain"
              v-bind="props"
              class="text-grey-darken-3"
              icon="mdi-map-legend"
              @click="showLegend = !showLegend"
            ></v-btn>
          </div>
        </template>
        <span>Toggle Legend</span>
      </v-tooltip>
      <v-tooltip>
        <template v-slot:activator="{ props }">
          <div v-bind="props" class="d-inline-block">
            <v-btn
              :disabled="allTraces.length === 0"
              variant="plain"
              v-bind="props"
              icon="mdi-download"
              @click="exportChartToHTML(allTraces, { dragmode: 'pan' })"
            ></v-btn>
          </div>
        </template>
        <span>Export to HTML</span>
      </v-tooltip>

      <v-tooltip text="Reset Drawer Size">
        <template v-slot:activator="{ props }">
          <v-btn
            variant="plain"
            v-bind="props"
            icon="mdi-resize"
            @click="projectStore.resetGraphSize(graphBarConfig)"
          ></v-btn>
        </template>
      </v-tooltip>
    </div>
    <div class="fill-height">
      <v-card class="h-100" :disabled="writeLoading" :flat="true">
        <v-lazy>
          <v-progress-linear
            v-if="writeLoading"
            class="w-100"
            color="primary"
            indeterminate
          ></v-progress-linear>
          <div
            class="h-100 d-flex justify-center align-center"
            v-else-if="Object.keys(visualiseItem).length === 0"
          >
            <v-alert variant="flat" color="#2AA18D" type="info" density="compact" class="ma-3">
              Please select a section line to generate the visualisation
            </v-alert>
          </div>
        </v-lazy>
        <div class="fill-height">
          <PlotlyLineChart
            ref="plotlyChart"
            @set-all-traces="allTraces = $event"
            @set-layout="layout = $event"
            :show-legend="showLegend"
            :key="`visualisation-${visualiseItem.id}-${forceReloadKey}`"
          />
        </div>
      </v-card>
    </div>
  </div>
</template>

<style scoped>
  .profile-graph-container {
    transition: border-bottom-color 0.3s;
    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  }
</style>
